import React from 'react';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { Account, IFormulaToAccount, IRiskTolerance } from '@sp-types/accountTypes';
import { getNetExposure, IExposureHistory } from '@utils/exposureUtils';
import { LinearGauge } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import { INetExposure } from '@sp-types/netExposureType';
import { SkeletonList } from './StrategiesUtils';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { FormatToShortPriceTemplate } from '../../../utils/FormatValue';
import { getExposureColor, getExposureStatus } from '../../../utils/RiskUtils';
import { Material } from '@sp-types/materialType';
import { ExposureSnapshot } from '../../../services/exposureService';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { useGeneralRisk } from '../../../hooks/useGeneralRisk';

interface Props {
  formulaId: string;
  accountMaterial?: IFormulaToAccount;
  exposureThresholds?: { dangerLow: number; warnLow: number; warnHigh: number; dangerHigh: number };
  netExposure?: INetExposure;
  materialsMap: Record<string, Material>;
  account: Account;
  latestSnapshot?: ExposureSnapshot;
  selectedSnapshot?: IExposureHistory;
}

const fmt = (v: number) => FormatToShortPriceTemplate(v);
const fmtMt = (v: number) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} MT`;

const formatDate = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

export const ExposureSummary: React.FC<Props> = ({
  formulaId,
  accountMaterial,
  exposureThresholds,
  netExposure,
  materialsMap,
  account,
  latestSnapshot,
  selectedSnapshot,
}) => {
  const { convertionMap, pricesMap } = useCurrencyRates();
  const terms = useCategoryTerms();
  const isGeneral = formulaId === GENERAL_ID;
  const [selectedPercentile, setSelectedPercentile] = React.useState<number>(5);

  const hasSelection = !!selectedSnapshot;
  const price = isGeneral ? 1 : (pricesMap[formulaId] || 1);

  // Compute net exposure from either the clicked snapshot or the latest one
  const currNetExposure = React.useMemo(() => {
    if (hasSelection) {
      const net = selectedSnapshot.futureOrders - selectedSnapshot.inventory - selectedSnapshot.financialPosition;
      return net * price;
    }
    if (latestSnapshot?.netExposureBase != null) return latestSnapshot.netExposureBase;
    if (netExposure) return getNetExposure(netExposure, convertionMap, formulaId, isGeneral);
    return null;
  }, [hasSelection, selectedSnapshot, latestSnapshot, netExposure, convertionMap, formulaId, isGeneral, price]);

  const riskTolerance: IRiskTolerance | undefined = React.useMemo(() => {
    if (!exposureThresholds) return undefined;
    return {
      dangerMinus: exposureThresholds.dangerLow,
      warnMinus: exposureThresholds.warnLow,
      warnPlus: exposureThresholds.warnHigh,
      dangerPlus: exposureThresholds.dangerHigh,
    };
  }, [exposureThresholds]);

  const exposureColor = React.useMemo(
    () =>
      riskTolerance && currNetExposure != null
        ? getExposureColor(getExposureStatus(riskTolerance, currNetExposure))
        : undefined,
    [currNetExposure, riskTolerance],
  );

  const generalRisk = useGeneralRisk();

  const volObj = React.useMemo(() => {
    const volatility =
      isGeneral ? generalRisk.volatility : materialsMap[formulaId]?.volatility;

    // VaR calculation: |exposure| × Z × σ
    const zScores: Record<number, number> = { 5: 1.645, 10: 1.282, 25: 0.674 };
    const z = zScores[selectedPercentile] || 1.645;
    const sigma = typeof volatility === 'number' ? volatility : 0;
    const exposure = currNetExposure ?? 0;
    const lossPotential = exposure !== 0 && sigma
      ? FormatToShortPriceTemplate(Math.abs(exposure) * z * sigma)
      : undefined;

    return { volatility, lossPotential };
  }, [materialsMap, formulaId, currNetExposure, generalRisk, isGeneral, selectedPercentile]);

  // Build breakdown from selected snapshot history point, or from latest full snapshot
  const breakdown = React.useMemo(() => {
    const showMt = !isGeneral;

    if (hasSelection) {
      const s = selectedSnapshot;
      const netQty = s.futureOrders - s.inventory - s.financialPosition;
      return [
        { label: terms.demand, mt: s.futureOrders, value: s.futureOrders * price, color: '#EF4444' },
        { label: `${terms.supply} (total)`, mt: s.inventory, value: s.inventory * price, color: '#22C55E' },
        { label: terms.hedge, mt: s.financialPosition, value: s.financialPosition * price, color: '#8B5CF6' },
        { label: 'Net Exposure', mt: netQty, value: netQty * price, color: '#0F172A', bold: true },
      ].map(row => ({ ...row, showMt }));
    }

    if (!latestSnapshot) return null;
    const s = latestSnapshot;
    return [
      { label: terms.demand, mt: s.demandQuantityMt, value: s.demandValueBase, color: '#EF4444' },
      { label: 'On-Hand', mt: s.onHandQuantityMt, value: s.onHandValueBase, color: '#22C55E' },
      { label: 'Purchase Orders', mt: s.poQuantityMt, value: s.poValueBase, color: '#3B82F6' },
      { label: terms.hedge, mt: s.hedgeQuantityMt, value: s.hedgeValueBase, color: '#8B5CF6' },
      { label: 'Net Exposure', mt: s.netQuantityMt, value: s.netExposureBase, color: '#0F172A', bold: true },
    ].map(row => ({ ...row, showMt }));
  }, [latestSnapshot, selectedSnapshot, hasSelection, isGeneral, terms, price]);

  // Title: show date of selected point, or "Latest" with the snapshot date
  const titleSuffix = hasSelection
    ? formatDate(new Date(selectedSnapshot.createDate))
    : latestSnapshot?.snapshotDate
      ? `Latest — ${formatDate(new Date(latestSnapshot.snapshotDate))}`
      : 'Latest';

  return (
    <div className="flex flex-col flex-1 bg-white shadow-md rounded-xl overflow-y-auto p-4">
      {/* Header — matches other policy detail panels */}
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
        <span className="text-base font-semibold text-gray-900">{titleSuffix}</span>
        <span className="text-sm text-gray-400">Exposure</span>
      </div>
      <div className="mb-6">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Net Exposure</div>
        <div className="text-2xl font-semibold mt-1" style={{ color: exposureColor || '' }}>
          {currNetExposure != null ? fmt(currNetExposure) : '—'}
        </div>
      </div>

      {/* Linear gauge — only when thresholds are configured */}
      {riskTolerance && (
        <div className="mb-4">
          <LinearGauge riskTolerance={riskTolerance} currentValue={currNetExposure || 0} />
        </div>
      )}

      {/* Categories breakdown */}
      {breakdown && (
        <div className="mb-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Breakdown</div>
          <div className="space-y-1.5">
            {breakdown.map(row => (
              <div key={row.label} className={`flex items-center justify-between text-sm ${row.bold ? 'font-semibold border-t border-gray-200 pt-1.5 mt-1' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: row.color }} />
                  <span className="text-gray-700">{row.label}</span>
                </div>
                <div className="flex items-center gap-3 text-right">
                  {row.showMt && <span className="text-gray-500 text-xs w-20">{fmtMt(row.mt)}</span>}
                  <span className="text-gray-900 w-24">{fmt(row.value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Volatility & potential loss */}
      <div className="flex flex-col gap-2 text-sm">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Volatility</div>
          <div className="font-medium">{typeof volObj.volatility === 'number' ? `${(volObj.volatility * 100).toFixed(1)}%` : '—'}</div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Loss potential</span>
            <ToggleChips
              selected={selectedPercentile}
              setSelected={setSelectedPercentile}
              chips={[
                { key: '5%', value: 5 },
                { key: '10%', value: 10 },
                { key: '25%', value: 25 },
              ]}
            />
          </div>
          <div className="font-medium">{volObj.lossPotential || '—'}</div>
        </div>
      </div>
    </div>
  );
};
